/**
 * scopeCSS — runtime CSS isolation.
 *
 * Each hand-built page in the trex-full-site-emergent-upgrade zip ships
 * its own self-contained stylesheet that includes top-level `:root`,
 * `*`, `body`, `html` resets and a domain-specific palette / typography.
 * Injecting it raw into the document would clobber the rest of the app.
 *
 * `scopeCSS(css, scope)` prefixes every top-level selector with
 * `.<scope>`, collapses `:root` / `body` onto the wrapper itself, drops
 * `html` rules (they would mutate the global document), keeps
 * `@keyframes` / `@font-face` verbatim and recurses into `@media` and
 * `@supports` blocks. The result is a stylesheet that ONLY affects
 * descendants of `<div class="<scope>">`.
 */

export function scopeCSS(css, scope) {
  // strip CSS comments
  css = css.replace(/\/\*[\s\S]*?\*\//g, "");

  let out = "";
  let i = 0;
  const len = css.length;

  function findMatchingBrace(start) {
    let depth = 0;
    for (let j = start; j < len; j++) {
      if (css[j] === "{") depth++;
      else if (css[j] === "}") {
        depth--;
        if (depth === 0) return j;
      }
    }
    return len;
  }

  while (i < len) {
    while (i < len && /\s/.test(css[i])) i++;
    if (i >= len) break;

    const start = i;
    while (i < len && css[i] !== "{" && css[i] !== ";") i++;

    if (css[i] === ";") {
      // at-rule without body (e.g. @import) — keep verbatim
      out += css.substring(start, i + 1) + "\n";
      i++;
      continue;
    }
    if (i >= len) break;

    const selectorRaw = css.substring(start, i).trim();
    const bodyStart = i;
    const bodyEnd = findMatchingBrace(bodyStart);
    const body = css.substring(bodyStart, bodyEnd + 1);

    if (
      selectorRaw.startsWith("@keyframes") ||
      selectorRaw.startsWith("@-webkit-keyframes") ||
      selectorRaw.startsWith("@font-face") ||
      selectorRaw.startsWith("@page") ||
      selectorRaw.startsWith("@counter-style") ||
      selectorRaw.startsWith("@property")
    ) {
      out += selectorRaw + body + "\n";
    } else if (
      selectorRaw.startsWith("@media") ||
      selectorRaw.startsWith("@supports") ||
      selectorRaw.startsWith("@container")
    ) {
      const inner = body.slice(1, -1);
      out += selectorRaw + " {\n" + scopeCSS(inner, scope) + "\n}\n";
    } else if (
      selectorRaw === "html" ||
      selectorRaw.startsWith("html ") ||
      selectorRaw.startsWith("html.") ||
      selectorRaw.startsWith("html[")
    ) {
      // drop global html rules (would affect the whole app)
    } else {
      const parts = selectorRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const prefixed = parts.map((s) => {
        if (s === ":root" || s === "body") return "." + scope;
        if (s === "*") return "." + scope + " *";
        if (s.startsWith("*::") || s.startsWith("*:") || s.startsWith("*[")) {
          return "." + scope + " " + s;
        }
        if (s.startsWith("body")) return "." + scope + s.slice(4);
        if (s.startsWith(":root")) return "." + scope + s.slice(5);
        if (s.startsWith("." + scope)) return s;
        return "." + scope + " " + s;
      });
      out += prefixed.join(", ") + " " + body + "\n";
    }

    i = bodyEnd + 1;
  }
  return out;
}
